import { useEffect, useState } from 'react';
import type { BenchmarkRatePeriod } from '../types/BenchmarkRatePeriod';
import { getBenchmarkRatePeriods } from '../api/benchmarkRatePeriods';

// Module-level cache shared by every hook caller for the lifetime of the page.
// `inflight` deduplicates concurrent fetches; cleared on failure so the next
// mount retries. Fetched LAZILY — only when this hook first runs (i.e. when
// AssetsPage mounts); never part of any login/startup preload. Cleared on
// logout via clearBenchmarkRatePeriodsCache() — next mount re-fetches.
let cache: BenchmarkRatePeriod[] | null = null;
let inflight: Promise<BenchmarkRatePeriod[]> | null = null;

function loadBenchmarkRatePeriods(): Promise<BenchmarkRatePeriod[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getBenchmarkRatePeriods()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearBenchmarkRatePeriodsCache(): void {
  cache = null;
  inflight = null;
}

interface UseBenchmarkRatePeriodsResult {
  data: BenchmarkRatePeriod[] | null;
  loading: boolean;
  error: string | null;
}

export function useBenchmarkRatePeriods(): UseBenchmarkRatePeriodsResult {
  const [state, setState] = useState<UseBenchmarkRatePeriodsResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      // Cache may have populated between render and effect (e.g. a sibling
      // instance finished fetching). Adopt it; bail if state already matches.
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadBenchmarkRatePeriods()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
